import { ExpressionRuntime } from '@gitbook/expr';
import { Trace } from '@gitbook/util-debug';

import {
    type Revision,
    type RevisionPage,
    type RevisionPageParent,
    type RevisionPages,
    RevisionPageType,
} from '../../types';
import { mutations } from '../../utils';
import {
    getPageParentsIndexInImmutableRevision,
    RootParentSymbol,
} from './getPageParentInRevision';
import { updatePageInRevision } from './updatePageInRevision';

export function getPageConditionsInRevision(revision: Revision, pageId: string): string[] {
    const parents = getPageParentsIndexInImmutableRevision(revision);
    const conditions: string[] = [];
    let currentPageId: string | typeof RootParentSymbol | undefined = pageId;

    while (currentPageId && currentPageId !== RootParentSymbol) {
        const page: RevisionPage | undefined = revision.pages[currentPageId];
        if (page?.condition) {
            conditions.unshift(page.condition);
        }
        currentPageId = page ? parents.get(page.id) : undefined;
    }

    return conditions;
}

/**
 * Update the conditional expression of a page.
 */
export function updatePageConditionInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    conditionalExpr: string
) {
    updatePageInRevision(revision, pageId, { condition: conditionalExpr });
}

/**
 * Remove onditional expression of a page.
 */
export function removePageConditionInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string
): void {
    updatePageInRevision(revision, pageId, (page) => {
        const condition = mutations.get(page, 'condition');
        if (!condition) {
            return;
        }

        mutations.del(page, 'condition');
    });
}

/**
 * Produces a filtered version of the provided revision, including only the pages whose conditional expressions evaluate to true.
 *
 * - If a page has no conditional expression, it is included in the resulting revision by default.
 * - If a page has a conditional expression, it is evaluated, and the page is included only if the evalution of the expression returns `true`.
 * - If the conditional expression of a group page, or pages with descendants, evaluates to `false`, the page group and all its descendants are excluded.
 */
export function evaluateRevision({
    revision: originalRevision,
    inputValues,
    runtime = new ExpressionRuntime(),
}: {
    revision: Revision;
    inputValues: object;
    runtime?: ExpressionRuntime;
}): Revision {
    const trace = new Trace('hive.evaluateRevision');

    return trace.wrapSync(() => {
        /**
         * The original root pages. This is used solely in the computation for
         * membership checks.
         */
        const originalRootPagesSet = new Set(originalRevision.rootPages);

        /**
         * The pages to include that passes the condition evaluation checks and will be
         * included in the final revision.
         */

        const includedPages: RevisionPages = {};

        /**
         * The root pages that needs to be included in the final revision. This is NOT ordered and
         * used solely for membership checks at the end for reconstructing the final root pages (with
         * the ones that didn't pass the condition evaluation checks excluded) but by preserving the original
         * order.
         */
        const includedRootPagesSet = new Set<string>();

        /**
         * The revision descendant pages for all pages. This is NOT ordered and used solely in the computation for
         * membership checks.
         */
        const excludedDescendantsSet = new Set<string>();

        // Walk the page tree starting from rootPages to ensure parents are always
        // processed before their children. Using Object.entries(revision.pages) is
        // incorrect because insertion order can place children before their conditional
        // parent, causing them to escape exclusion.
        const pageQueue = [...originalRevision.rootPages];
        for (let i = 0; i < pageQueue.length; i++) {
            const pageId = pageQueue[i];
            const page = originalRevision.pages[pageId];
            if (!page) {
                continue;
            }

            if (excludedDescendantsSet.has(pageId)) {
                // No need to evaluate the page as its a descendant of a page that is excluded
                // from the final revision but add its direct descendant to the excludedDescendants set
                if (isRevisionPageParentWithChildren(page)) {
                    page.pages.forEach((directDescendant) =>
                        excludedDescendantsSet.add(directDescendant)
                    );
                }
                continue;
            }

            const includePage =
                !page.condition || runtime.evaluateBoolean(page.condition, inputValues);
            if (!includePage) {
                // For excluded pages with descendants add their direct descendants to the excludedDescendants set
                // so we can skip their condition evaluation when encountering them.
                if (isRevisionPageParentWithChildren(page)) {
                    page.pages.forEach((directDescendant) =>
                        excludedDescendantsSet.add(directDescendant)
                    );
                }
                continue;
            }

            includedPages[page.id] = page;
            if (originalRootPagesSet.has(page.id)) {
                includedRootPagesSet.add(page.id);
            }

            // Enqueue children for processing
            if (isRevisionPageParentWithChildren(page)) {
                pageQueue.push(...page.pages);
            }
        }

        // Adjust the children of pages with direct descendants as some of their descendant may
        // have been excluded.
        const includedPagesWithUpdatedChildren: RevisionPages = {};

        Object.entries(includedPages).forEach(([pageId, page]) => {
            if (isRevisionPageParentWithChildren(page)) {
                includedPagesWithUpdatedChildren[pageId] = {
                    ...page,
                    pages: page.pages.filter((childId) => childId in includedPages),
                };
                return;
            }

            includedPagesWithUpdatedChildren[pageId] = page;
        });

        // Maintain the order of the root pages, only including those that passed the computation check.
        const includedRootPages = originalRevision.rootPages.filter((pageId) =>
            includedRootPagesSet.has(pageId)
        );
        return {
            ...originalRevision,
            rootPages: includedRootPages,
            pages: includedPagesWithUpdatedChildren,
        };
    });
}

function isRevisionPageParentWithChildren(page: RevisionPage): page is RevisionPageParent {
    return (
        (page.type === RevisionPageType.Group || page.type === RevisionPageType.Document) &&
        page.pages.length > 0
    );
}
